/**
 * Global request cache and deduplication utility
 * Prevents duplicate API calls across components
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

// Global cache storage
const cache: Map<string, CacheEntry<unknown>> = new Map();

// Track in-flight requests to prevent duplicate concurrent fetches
const inFlightRequests: Map<string, Promise<unknown>> = new Map();

const DEFAULT_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch with caching and request deduplication
 * @param key - Unique cache key for this request
 * @param fetcher - Async function that performs the actual fetch
 * @param cacheDuration - How long to cache the result (ms)
 */
export async function fetchWithCache<T>(
  key: string,
  fetcher: () => Promise<T>,
  cacheDuration: number = DEFAULT_CACHE_DURATION
): Promise<T> {
  const now = Date.now();

  // Check cache first
  const cached = cache.get(key) as CacheEntry<T> | undefined;
  if (cached && now - cached.timestamp < cacheDuration) {
    return cached.data;
  }

  // If there's already a request in flight for this key, return that promise
  if (inFlightRequests.has(key)) {
    return inFlightRequests.get(key) as Promise<T>;
  }

  // Create new request and track it
  const fetchPromise = (async () => {
    try {
      const data = await fetcher();

      // Update cache
      cache.set(key, {
        data,
        timestamp: Date.now(),
      });

      return data;
    } finally {
      // Clean up in-flight tracking
      inFlightRequests.delete(key);
    }
  })();

  inFlightRequests.set(key, fetchPromise);
  return fetchPromise;
}

/**
 * Get cached data if available (without fetching)
 */
export function getCached<T>(
  key: string,
  cacheDuration: number = DEFAULT_CACHE_DURATION
): T | null {
  const cached = cache.get(key) as CacheEntry<T> | undefined;
  if (cached && Date.now() - cached.timestamp < cacheDuration) {
    return cached.data;
  }
  return null;
}

/**
 * Check if cache is valid for a key
 */
export function isCacheValid(
  key: string,
  cacheDuration: number = DEFAULT_CACHE_DURATION
): boolean {
  const cached = cache.get(key);
  return cached !== undefined && Date.now() - cached.timestamp < cacheDuration;
}

/**
 * Invalidate cache for a specific key
 */
export function invalidateCache(key: string): void {
  cache.delete(key);
}

/**
 * Invalidate all cache entries matching a prefix
 */
export function invalidateCacheByPrefix(prefix: string): void {
  Array.from(cache.keys()).forEach((key) => {
    if (key.startsWith(prefix)) {
      cache.delete(key);
    }
  });
}

/**
 * Clear all cache
 */
export function clearAllCache(): void {
  cache.clear();
}

/**
 * Fetch with request deduplication only (no caching)
 * Prevents duplicate concurrent requests but doesn't cache the result
 * @param key - Unique key for this request
 * @param fetcher - Async function that performs the actual fetch
 */
export async function fetchWithDedup<T>(
  key: string,
  fetcher: () => Promise<T>
): Promise<T> {
  // If there's already a request in flight for this key, return that promise
  if (inFlightRequests.has(key)) {
    return inFlightRequests.get(key) as Promise<T>;
  }

  // Create new request and track it
  const fetchPromise = (async () => {
    try {
      return await fetcher();
    } finally {
      // Clean up in-flight tracking
      inFlightRequests.delete(key);
    }
  })();

  inFlightRequests.set(key, fetchPromise);
  return fetchPromise;
}
